import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DashLayout } from "@/components/DashLayout";
import { Pill } from "@/components/Tables";
import { Field } from "@/pages/user/UserPayouts";
import { api, errMsg, inr } from "@/lib/api";
import { ADMIN_NAV } from "@/pages/navs";

const EMPTY = { name: "", email: "", username: "", phone: "", business_name: "", password: "", upi_id: "" };
const FIELDS = [["name", "Full name"], ["business_name", "Business name"], ["email", "Email", "email"], ["username", "Username (for pay link)"], ["phone", "Phone"], ["upi_id", "UPI ID"], ["password", "Password", "password"]];

const CreateUser = ({ open, setOpen, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/admin/users", f);
      toast.success("Merchant created"); setF(EMPTY); setOpen(false); onDone();
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle className="font-display">Create merchant</DialogTitle><DialogDescription>Merchant can login with this email and password.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([k, l, t]) => (
            <Field key={k} label={l}>
              <Input data-testid={`create-user-${k}-input`} type={t || "text"} required={["name", "email", "username", "password"].includes(k)} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            </Field>
          ))}
          <button data-testid="create-user-submit-btn" disabled={busy} className="flex h-10 items-center justify-center gap-2 rounded-md bg-[#2563EB] font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-60 sm:col-span-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create merchant
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setRows(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <DashLayout nav={ADMIN_NAV} title="Merchants" subtitle="Create and manage merchant accounts"
      actions={<button data-testid="open-create-user-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-md bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1D4ED8]"><Plus className="h-4 w-4" /><span className="hidden sm:inline">Create user</span></button>}>
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="users-search-input" placeholder="Search name, email, username..." value={q} onChange={(e) => setQ(e.target.value)} className="bg-white pl-10" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="users-table">
        <Table>
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Merchant</TableHead><TableHead>Username</TableHead><TableHead className="text-right">Collection</TableHead>
            <TableHead className="text-right">Payout</TableHead><TableHead className="text-right">Chargeback</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-slate-400">No merchants found</TableCell></TableRow>}
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} onClick={() => navigate(`/admin/users/${u.id}`)} className="cursor-pointer">
                <TableCell className="whitespace-nowrap"><div className="font-medium">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></TableCell>
                <TableCell className="font-mono text-xs">{u.username}</TableCell>
                <TableCell className="whitespace-nowrap text-right">{inr(u.collection_balance)}</TableCell>
                <TableCell className="whitespace-nowrap text-right">{inr(u.payout_balance)}</TableCell>
                <TableCell className="whitespace-nowrap text-right">{inr(u.chargeback_balance)}</TableCell>
                <TableCell><Pill className={u.status === "blocked" ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}>{u.status}</Pill></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CreateUser open={open} setOpen={setOpen} onDone={load} />
    </DashLayout>
  );
}
